/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 17 2025
 **/

import { useRef, useEffect } from "react";
import videojs from "video.js";

import useClickWithoutDrag from "../hooks/useClickWithoutDrag";
import { VideoRaycastTarget } from "./emotion";
import "video.js/dist/video-js.css";

interface IVideoPlayerMobileProps {
	options: videojs.PlayerOptions;
	onLoad: () => void;
}

const initialOptions: videojs.PlayerOptions = {
	controls: true,
	fluid: true,
	controlBar: {
		volumePanel: {
			inline: false
		},
		fullscreenToggle: false
	},
	aspectRatio: "9:16",
	preload: "auto",
	autoplay: "any",
	loop: false,
	muted: true,
	width: 720,
	height: 1280,
	html5: {
		hls: {
			overrideNative: true
		},
		nativeVideoTracks: false,
		nativeAudioTracks: false,
		nativeTextTracks: false
	}
};

export default function VideoPlayerMobile({ options, onLoad }: IVideoPlayerMobileProps) {
	const videoNode = useRef<HTMLVideoElement>(null);
	const player = useRef<videojs.Player>();
	const { onPointerDown, onPointerUp } = useClickWithoutDrag();

	useEffect(() => {
		if (videoNode.current) {
			videoNode.current.setAttribute("playsinline", "true");
			player.current = videojs(videoNode.current, {
				...initialOptions,
				...options
			}).ready(function () {
				onLoad();
			});
		}
		return () => player.current && player.current.dispose();
	}, [options]);

	/* Update from UI */
	const handleOnClick = () => {
		if (videoNode.current) {
			if (videoNode.current.paused) videoNode.current.play();
			else videoNode.current.pause();
		}
	};

	return (
		<div data-vjs-player style={{ backgroundColor: "transparent" }}>
			<video ref={videoNode} className="video-js vjs-waiting" poster="" />
			<style>
				{`
						.vjs-big-play-button {
							display: none !important;
						}

						.video-js .vjs-tech {
							position: fixed;
						}

						.vjs-picture-in-picture-control {
							display: none !important;
						}
					`}
			</style>
			<VideoRaycastTarget onPointerDown={onPointerDown} onPointerUp={(e) => onPointerUp(e, handleOnClick)} />
		</div>
	);
}
